import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import "chart.js/auto";
import { Bar } from "react-chartjs-2";

import CustomSelect from "../../../Components/common/CustomSelect/CustomSelect";
import TableSpinnerIcon from "../../../Components/Icon-SVG/TableSpinnerIcon";
import {
  DoughnutWithLegend,
  KpiTile,
  NoData,
  RankList,
  Widget,
  percent,
  useThemeColors,
} from "../../../Components/common/DashboardWidgets/DashboardWidgets";
import SankeyChart from "../../../Components/common/DashboardWidgets/SankeyChart";
import {
  WINDOW_OPTIONS,
  bucketLabel,
  formatDate,
  storedWindow,
} from "../../../Components/common/DashboardWidgets/dashboardUtils";
import { RequestThreatHunterDashboard } from "../../../store/actions/integrationsActions";

import "./styles.scss";

const PERIOD_SELECT_ID = "threatHunterDashboardPeriod";
const DEFAULT_PERIOD = WINDOW_OPTIONS[0];
const DEFAULT_PERIOD_INDEX = 0;

const RISK_LEVELS = [
  { id: "high", label: "High risk", color: "#f46a6a" },
  { id: "medium", label: "Medium risk", color: "#f1b44c" },
  { id: "low", label: "Low risk", color: "#34c38f" },
];
const RISK_COLOR = Object.fromEntries(RISK_LEVELS.map((level) => [level.id, level.color]));
const SEVERITY_COLOR = { high: RISK_COLOR.high, medium: RISK_COLOR.medium, low: RISK_COLOR.low };

const ENDPOINT_COLOR = "#36a2eb";
const CATEGORY_COLOR = "#a77bdb";
const OTHER_COLOR = "#74788d";

const PASS_COLOR = RISK_COLOR.low;
const FAIL_COLOR = RISK_COLOR.high;
const OS_COLORS = ["#36a2eb", "#a77bdb", "#4bc0c0", "#f1b44c"];
const CONTROL_STATES = {
  pass: { label: "Pass", color: PASS_COLOR },
  fail: { label: "Fail", color: FAIL_COLOR },
};

const reportPath = (reportId) => `/analysis-reports?data=${reportId}&type=threat-hunter`;

// Node names are the ids, so the colours are looked up by name
const sankeyColors = (nodes) =>
  Object.fromEntries(
    nodes.map((node) => {
      if (node.group === "verdict") {
        const level = RISK_LEVELS.find((item) => item.label === node.name);
        return [node.name, level?.color || OTHER_COLOR];
      }
      if (node.name.startsWith("Other ")) return [node.name, OTHER_COLOR];
      return [node.name, node.group === "source" ? ENDPOINT_COLOR : CATEGORY_COLOR];
    }),
  );

const RiskBadge = ({ level, score }) => (
  <span className={`th-risk th-risk--${level}`}>
    {RISK_LEVELS.find((item) => item.id === level)?.label || level}
    {score !== undefined && <b>{score}</b>}
  </span>
);

// One row per control: name, a bar split into pass / fail / not reported and
// the pass rate of the endpoints that report it
const CoverageList = ({ rows, emptyText }) => {
  if (!rows.length) return <NoData text={emptyText} />;
  return (
    <ul className="th-coverage">
      {rows.map((row) => {
        const total = row.segments.reduce((sum, segment) => sum + segment.value, 0);
        const title = row.segments.map((segment) => `${segment.value} ${segment.label}`).join(", ");
        return (
          <li key={row.name} className="th-coverage__row" title={`${row.name}: ${title}`}>
            <span className="th-coverage__name">{row.name}</span>
            <span className="th-coverage__rate" style={{ color: row.rateColor }}>
              {row.rate}%
            </span>
            <span className="th-coverage__track">
              {row.segments.map(
                (segment) =>
                  segment.value > 0 && (
                    <span
                      key={segment.label}
                      style={{ width: `${(segment.value / total) * 100}%`, backgroundColor: segment.color }}
                    />
                  ),
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

const CoverageLegend = ({ items }) => (
  <ul className="th-coverage__legend">
    {items.map((item) => (
      <li key={item.label}>
        <span style={{ backgroundColor: item.color }} />
        {item.label}
      </li>
    ))}
  </ul>
);

// A dot per security control of the endpoint's latest scan
const ControlDots = ({ controls }) => (
  <span className="th-dots">
    {controls.map((control) => {
      const state = CONTROL_STATES[control.state];
      return (
        <span
          key={control.id}
          className="th-dots__dot"
          style={state ? { backgroundColor: state.color, borderColor: state.color } : undefined}
          title={`${control.name}: ${state?.label || "Not reported"}`}
        />
      );
    })}
  </span>
);

const ruleRate = (good, bad) => (good + bad ? Math.round((good / (good + bad)) * 100) : 0);
const rateColor = (rate) => (rate >= 90 ? PASS_COLOR : rate >= 60 ? RISK_COLOR.medium : FAIL_COLOR);

const ThreatHunterDashboard = () => {
  const dispatch = useDispatch();
  const theme = useThemeColors();
  const data = useSelector((state) => state.integrations.threatHunterDashboard);
  const loading = useSelector((state) => state.integrations.threatHunterDashboardLoading);

  const [period, setPeriod] = useState(() => storedWindow(PERIOD_SELECT_ID, DEFAULT_PERIOD));

  // Keyed by the id, so only a real change of period loads the dashboard again
  const periodId = period.id;
  const loadDashboard = useCallback(() => {
    dispatch(RequestThreatHunterDashboard({ filterByLast: periodId }));
  }, [dispatch, periodId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const emptyText = loading ? "Loading..." : "No data in this period";
  const unit = data?.timeline?.unit || (periodId === "24-hour" ? "hour" : "day");
  const totals = data?.totals || {};
  const posture = data?.posture;
  const yara = data?.yara;
  const sankey = data?.sankey || { nodes: [], links: [] };
  const colors = useMemo(() => sankeyColors(sankey.nodes), [sankey.nodes]);

  const buckets = useMemo(() => data?.timeline?.buckets || [], [data]);
  const hasTimeline = buckets.some((bucket) => RISK_LEVELS.some((level) => bucket[level.id]));

  const timelineChart = useMemo(() => {
    const axis = {
      ticks: { color: theme.text, font: { size: 11 } },
      grid: { color: theme.grid, drawTicks: false },
      border: { display: false },
    };
    return {
      data: {
        labels: buckets.map((bucket) => bucketLabel(bucket.key, unit)),
        datasets: RISK_LEVELS.map((level) => ({
          label: level.label,
          data: buckets.map((bucket) => bucket[level.id] || 0),
          backgroundColor: level.color,
          borderRadius: 3,
          maxBarThickness: 26,
          stack: "reports",
        })),
      },
      options: {
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            position: "bottom",
            labels: { color: theme.text, boxWidth: 12, boxHeight: 12, useBorderRadius: true },
          },
        },
        scales: {
          x: {
            ...axis,
            stacked: true,
            grid: { display: false },
            ticks: { ...axis.ticks, maxRotation: 0, autoSkipPadding: 12 },
          },
          y: { ...axis, stacked: true, beginAtZero: true, ticks: { ...axis.ticks, precision: 0 } },
        },
      },
    };
  }, [buckets, unit, theme]);

  const osItems = (data?.operatingSystems || []).map((os, index) => ({
    label: os.name,
    value: os.endpoints,
    color: os.name === "Other OS" || os.name === "Unknown" ? OTHER_COLOR : OS_COLORS[index % OS_COLORS.length],
  }));
  const unsupported = (data?.operatingSystems || []).reduce((sum, os) => sum + os.unsupported, 0);

  const controlRows = (posture?.controls || []).map((control) => {
    const rate = ruleRate(control.pass, control.fail);
    return {
      name: control.name,
      rate,
      rateColor: rateColor(rate),
      segments: [
        { label: "pass", value: control.pass, color: PASS_COLOR },
        { label: "fail", value: control.fail, color: FAIL_COLOR },
        { label: "not reported", value: control.unknown, color: OTHER_COLOR },
      ],
    };
  });
  const protectionRows = (posture?.protections || []).map((protection) => {
    const rate = ruleRate(protection.enabled, protection.disabled);
    return {
      name: protection.name,
      rate,
      rateColor: rateColor(rate),
      segments: [
        { label: "enabled", value: protection.enabled, color: PASS_COLOR },
        { label: "disabled", value: protection.disabled, color: FAIL_COLOR },
      ],
    };
  });
  const engines = (data?.detectionEngines || []).map((engine) => ({
    name: engine.name,
    count: engine.detections,
  }));

  const detections = (data?.topDetections || []).map((item) => ({
    name: item.name,
    count: item.hosts,
    color: SEVERITY_COLOR[item.severity],
  }));
  const issues = (data?.securityIssues || []).map((item) => ({
    name: item.name,
    count: item.hosts,
    color: SEVERITY_COLOR[item.severity],
  }));
  const yaraRules = (yara?.topRules || []).map((rule) => ({ name: rule.name, count: rule.hosts }));

  const viewAll = (to) => (
    <Link className="mgw-dashboard__link" to={to}>
      View all
    </Link>
  );

  return (
    <div className="mgw-dashboard th-dashboard">
      <div className="mgw-dashboard__header">
        <h1>Threat Hunter Dashboard</h1>
        <p>
          Endpoint risk from the latest threat hunting scan of each agent.
          {data?.truncated &&
            ` Based on the ${data.endpointsScanned.toLocaleString()} most recently scanned of ${data.endpointsTotal.toLocaleString()} endpoints.`}
        </p>
        <div className="mgw-dashboard__header__select">
          <p>Filter by last: </p>
          <CustomSelect
            id={PERIOD_SELECT_ID}
            data={WINDOW_OPTIONS}
            select={period}
            setSelected={setPeriod}
            defaultIndex={DEFAULT_PERIOD_INDEX}
            width={140}
          />
          <div className="mgw-dashboard__refresh" onClick={loadDashboard} title="Refresh">
            <TableSpinnerIcon
              className="container-services__spinner"
              customStyle={{ margin: "0", height: "20px" }}
              spin={loading}
            />
          </div>
        </div>
      </div>

      <div className="mgw-dashboard__grid">
        <div className="mgw-dashboard__kpis">
          <KpiTile
            label="Endpoints Scanned"
            value={(totals.endpoints ?? 0).toLocaleString()}
            hint={`${(totals.reports ?? 0).toLocaleString()} reports, ${period.label}`}
          />
          <KpiTile
            label="High Risk Endpoints"
            value={(totals.highEndpoints ?? 0).toLocaleString()}
            hint={`${percent(totals.highEndpoints, data?.endpointsScanned)}% of scanned`}
            tone="danger"
          />
          <KpiTile
            label="Medium Risk Endpoints"
            value={(totals.mediumEndpoints ?? 0).toLocaleString()}
            hint={`${percent(totals.mediumEndpoints, data?.endpointsScanned)}% of scanned`}
            tone="warning"
          />
          <KpiTile
            label="Active Threats"
            value={(totals.activeThreats ?? 0).toLocaleString()}
            hint={`Running on ${(totals.activeThreatEndpoints ?? 0).toLocaleString()} endpoints`}
            tone={totals.activeThreats ? "danger" : "default"}
          />
          <KpiTile
            label="Hardened Endpoints"
            value={posture?.assessed ? `${posture.hardened.toLocaleString()} / ${posture.assessed.toLocaleString()}` : "-"}
            hint={
              posture?.assessed
                ? `${percent(posture.hardened, posture.assessed)}% pass every control`
                : "No security ratings reported"
            }
            tone="info"
          />
          <KpiTile
            label="Yara Matches"
            value={yara ? yara.matches.toLocaleString() : "-"}
            hint={
              yara
                ? `${yara.hosts.toLocaleString()} endpoints, ${yara.rules.toLocaleString()} rules`
                : "Yara results unavailable"
            }
          />
        </div>

        <Widget
          title="Endpoint Threat Flow"
          subtitle="Detections in each endpoint's latest scan, by threat category and the scan's risk level"
          span={8}
        >
          {sankey.nodes.length ? (
            <SankeyChart
              nodes={sankey.nodes}
              links={sankey.links}
              colors={colors}
              height={320}
              unitOf={() => "detections"}
              ariaLabel="Endpoint threat flow"
            />
          ) : (
            <NoData text={loading ? emptyText : "No detections in this period"} />
          )}
        </Widget>

        <Widget
          title="Security Controls"
          subtitle="Pass rate of the endpoints that report each control"
          span={4}
        >
          <CoverageList rows={controlRows} emptyText={emptyText} />
          {controlRows.length > 0 && (
            <CoverageLegend
              items={[
                { label: "Pass", color: PASS_COLOR },
                { label: "Fail", color: FAIL_COLOR },
                { label: "Not reported", color: OTHER_COLOR },
              ]}
            />
          )}
        </Widget>

        <Widget
          title="Threat Reports Over Time"
          subtitle={`Reports per ${unit} by risk level`}
          span={8}
        >
          {hasTimeline ? (
            <div className="mgw-chart mgw-chart--tall">
              <Bar data={timelineChart.data} options={timelineChart.options} />
            </div>
          ) : (
            <NoData text={emptyText} />
          )}
        </Widget>

        <Widget
          title="Operating Systems"
          subtitle={
            unsupported
              ? `${unsupported.toLocaleString()} endpoint${unsupported === 1 ? "" : "s"} on a potentially unsupported OS`
              : "Endpoints scanned in this period"
          }
          span={4}
          className="mgw-dashboard__item--centered"
        >
          {osItems.length ? (
            <DoughnutWithLegend
              items={osItems}
              centerValue={(data?.endpointsScanned ?? 0).toLocaleString()}
              centerLabel="endpoints"
              stacked
            />
          ) : (
            <NoData text={emptyText} />
          )}
        </Widget>

        <Widget
          title="Riskiest Endpoints"
          subtitle="Latest scan of each endpoint, highest risk first"
          span={12}
          action={viewAll("/threat-reports")}
        >
          {data?.riskiestEndpoints?.length ? (
            <div className="mgw-table mgw-table--compact">
              <table>
                <colgroup>
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "15%" }} />
                  <col style={{ width: "150px" }} />
                  <col />
                  <col style={{ width: "100px" }} />
                  <col style={{ width: "110px" }} />
                  <col style={{ width: "175px" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Endpoint</th>
                    <th>OS</th>
                    <th>Risk</th>
                    <th>Activity</th>
                    <th>Threats</th>
                    <th>Controls</th>
                    <th>Last scan</th>
                  </tr>
                </thead>
                <tbody>
                  {data.riskiestEndpoints.map((endpoint) => (
                    <tr key={endpoint.reportId}>
                      <td>
                        <Link className="th-endpoint" to={reportPath(endpoint.reportId)}>
                          {endpoint.hostname || "Unknown host"}
                        </Link>
                        <small className="th-endpoint__meta">
                          {[endpoint.username, endpoint.ip].filter(Boolean).join(" · ")}
                        </small>
                      </td>
                      <td title={[endpoint.os, endpoint.osVersion].filter(Boolean).join(" ")}>
                        {endpoint.os || "-"}
                      </td>
                      <td>
                        <RiskBadge level={endpoint.level} score={endpoint.riskScore} />
                      </td>
                      <td title={endpoint.activity}>{endpoint.activity || "-"}</td>
                      <td>
                        <span className="th-threats">
                          <b>{endpoint.threats.toLocaleString()}</b>
                          {endpoint.highThreats > 0 && <span>{endpoint.highThreats} high</span>}
                        </span>
                      </td>
                      <td>
                        <ControlDots controls={endpoint.controls || []} />
                      </td>
                      <td title={`${endpoint.reports} report${endpoint.reports === 1 ? "" : "s"} in this period`}>
                        {formatDate(endpoint.reportTime)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <NoData text={loading ? emptyText : "No risky endpoints in this period"} />
          )}
        </Widget>

        <Widget
          title="Top Detections"
          subtitle="High and medium severity detections · endpoints affected"
          span={4}
        >
          <RankList items={detections} color={RISK_COLOR.high} emptyText={emptyText} />
        </Widget>

        <Widget
          title="Detection Engines"
          subtitle="Detections in the latest scans, by the engine that found them"
          span={4}
        >
          <RankList items={engines} color={ENDPOINT_COLOR} emptyText={emptyText} />
        </Widget>

        <Widget
          title="Endpoint Security Issues"
          subtitle="Weak settings found on endpoints · endpoints affected"
          span={4}
        >
          <RankList items={issues} color={RISK_COLOR.medium} emptyText={emptyText} />
        </Widget>

        <Widget
          title="Exploit Protections"
          subtitle="Windows security features enabled on the endpoints that report them"
          span={6}
        >
          <CoverageList rows={protectionRows} emptyText={emptyText} />
          {protectionRows.length > 0 && (
            <CoverageLegend
              items={[
                { label: "Enabled", color: PASS_COLOR },
                { label: "Disabled", color: FAIL_COLOR },
              ]}
            />
          )}
        </Widget>

        <Widget
          title="Top Yara Rules"
          subtitle="Custom rules that matched on most endpoints"
          span={6}
          action={viewAll("/yara-results")}
        >
          <RankList
            items={yaraRules}
            color={CATEGORY_COLOR}
            emptyText={yara ? "No yara matches in this period" : emptyText}
          />
        </Widget>
      </div>
    </div>
  );
};

export default ThreatHunterDashboard;
